// src/pages/Home/HomeAppliPage.jsx

import React, { useEffect, useState } from "react";
import {
  FaArrowRight,
  FaBookOpen,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaCogs,
  FaEuroSign,
  FaEye,
  FaImages,
  FaLaptopCode,
  FaLock,
  FaMobileAlt,
  FaPlayCircle,
  FaRegLightbulb,
  FaShieldAlt,
  FaStar,
  FaUsers,
  FaUserShield,
  FaChevronLeft,
  FaChevronRight,
  FaCircle,
  FaRegCircle,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";

import useScrollToTop from "../../../hooks/useScrollToTop";
import ScrollIndicator from "../../../components/ScrollIndicator/ScrollIndicator";
import Reveal from "../../../components/Reveal/Reveal";
import Footer from "../../Home/HomePage/Footer";

import "../../Home/HomePage/Home.css";
const HERO_BACKGROUND_IMAGE = "/images/fond01.webp";

const features = [
  {
    icon: FaMobileAlt,
    title: "Site vitrine responsive",
    text: "Une présentation moderne du club, lisible sur ordinateur, tablette et smartphone.",
  },
  {
    icon: FaCalendarAlt,
    title: "Horaires & tarifs",
    text: "Les familles retrouvent facilement les cours, les groupes, les lieux et les informations pratiques.",
  },
  {
    icon: FaUsers,
    title: "Inscriptions en ligne",
    text: "Un parcours simple pour transmettre les informations d’inscription au club.",
  },
  {
    icon: FaImages,
    title: "Actualités & galerie",
    text: "Mettez en avant la vie du club avec des articles, photos, événements et résultats.",
  },
  {
    icon: FaUserShield,
    title: "Dashboard administrateur",
    text: "Une interface claire pour gérer les contenus du site sans compétence technique.",
  },
  {
    icon: FaLock,
    title: "Mode démo sécurisé",
    text: "Les visiteurs peuvent découvrir le tableau de bord en lecture seule, sans modifier les données.",
  },
];

const modules = [
  "Page d’accueil moderne",
  "Présentation du club",
  "Disciplines / cours",
  "Horaires & tarifs",
  "Formulaire d’inscription",
  "Actualités",
  "Galeries photos",
  "Partenaires",
  "Formulaire de contact",
  "Dashboard administrateur",
  "Paramètres du club",
  "Maintenance des images",
];

const pricing = [
  {
    name: "Club Essentiel",
    price: "25 €",
    period: "/ an",
    description:
      "Pour les petits clubs qui veulent une présence web propre, simple et efficace.",
    items: [
      "Site vitrine responsive",
      "Pages club, horaires, tarifs",
      "Actualités et galerie photos",
      "Formulaire de contact",
      "Interface administrateur",
    ],
    highlighted: false,
  },
  {
    name: "Club Plus",
    price: "50 €",
    period: "/ an",
    description:
      "Pour les clubs qui veulent une solution plus complète avec inscription et suivi.",
    items: [
      "Toutes les fonctions essentielles",
      "Formulaire d’inscription",
      "Gestion plus avancée des contenus",
      "Accompagnement à la mise en route",
      "Support technique par email",
    ],
    highlighted: true,
  },
  {
    name: "Installation",
    price: "Sur devis",
    period: "",
    description:
      "Mise en place personnalisée selon votre club, vos contenus et votre besoin.",
    items: [
      "Paramétrage du club",
      "Ajout du logo et des couleurs",
      "Aide à la première mise en ligne",
      "Formation rapide en visio",
      "Conseils pour la communication",
    ],
    highlighted: false,
  },
];

const tutorialSteps = [
  {
    number: "01",
    title: "Je prépare les informations du club",
    text: "Logo, couleurs, horaires, tarifs, disciplines, contacts et photos principales.",
  },
  {
    number: "02",
    title: "Le site est configuré",
    text: "Les pages principales sont mises en place avec une interface adaptée à votre structure.",
  },
  {
    number: "03",
    title: "Vous gérez vos contenus",
    text: "Depuis le Dashboard Admin, vous pouvez mettre à jour les informations importantes.",
  },
  {
    number: "04",
    title: "Les familles consultent facilement",
    text: "Le site est accessible sur mobile avec les informations utiles pour découvrir et rejoindre le club.",
  },
];

export default function HomeAppliPage() {
  useScrollToTop();

  useEffect(() => {
    const scrollToCurrentHash = () => {
      const hash = window.location.hash?.replace("#", "");

      if (!hash) return;

      window.setTimeout(() => {
        const target = document.getElementById(hash);

        if (target) {
          target.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 120);
    };

    scrollToCurrentHash();

    window.addEventListener("hashchange", scrollToCurrentHash);

    return () => {
      window.removeEventListener("hashchange", scrollToCurrentHash);
    };
  }, []);

  const scrollToTarifs = () => {
    document.getElementById("tarifs")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const scrollToFeatures = () => {
    document.getElementById("fonctionnalites")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // 3) Ajoute ce bloc dans HomeAppliPage(), avant le return

  const screens = [
    {
      category: "Public",
      title: "Page d’accueil du club",
      description:
        "Une page d’accueil moderne avec hero visuel, accès rapide aux inscriptions, présentation du club et mise en avant des informations importantes.",
      image: "/images/diapo/image001.webp",
      comment:
        "Cette capture met en avant la vitrine publique du club, pensée pour rassurer les familles et donner une image moderne dès la première visite.",
    },
    {
      category: "Admin",
      title: "Tableau de bord administrateur",
      description:
        "Une vue d’ensemble claire pour retrouver rapidement les contenus du site, les paramètres et les outils d’administration.",
      image: "/images/diapo/image002.webp",
      comment:
        "Le dashboard centralise les accès utiles pour les bénévoles et responsables du club, avec une interface simple à prendre en main.",
    },
    {
      category: "Admin",
      title: "Gestion des actualités",
      description:
        "Création, modification et publication des actualités du club pour garder le site vivant toute l’année.",
      image: "/images/diapo/image003.webp",
      comment:
        "Les responsables peuvent publier facilement une nouvelle information, un événement ou un compte-rendu de compétition.",
    },
    {
      category: "Public",
      title: "Horaires & tarifs",
      description:
        "Une page lisible qui permet aux familles de retrouver facilement les créneaux, les groupes et les tarifs proposés par le club.",
      image: "/images/diapo/image004.webp",
      comment:
        "Cette section aide les visiteurs à comprendre rapidement l’offre du club sans devoir chercher l’information.",
    },
    {
      category: "Admin",
      title: "Galerie photos",
      description:
        "Un espace pour valoriser la vie du club à travers les photos, les événements et les moments forts de la saison.",
      image: "/images/diapo/image005.webp",
      comment:
        "Les galeries donnent de la vie au site et renforcent l’attractivité du club auprès des futurs adhérents.",
    },
    {
      category: "Public",
      title: "Présentation du club",
      description:
        "Un espace pour présenter les membres clés du club, les disciplines proposées, les valeurs et l’histoire du club.",
      image: "/images/diapo/image006.webp",
      comment:
        "Cette section permet aux visiteurs de mieux connaître le club et ses activités, renforçant ainsi l’attractivité auprès des futurs adhérents.",
    },
    {
      category: "Admin",
      title: "Gestion des adhérents inscrits",
      description:
        "Un espace pour gérer les informations des adhérents du club, suivre les inscriptions et les renouvellements.",
      image: "/images/diapo/image007.webp",
      comment:
        "Cette section permet aux administrateurs de gérer efficacement les adhérents et leurs informations. Sortie pdf ou excel pour faciliter le suivi administratif du club.",
    },
  ];

  const [activeScreen, setActiveScreen] = useState(0);

  const goToPrevScreen = () => {
    setActiveScreen((prev) => (prev === 0 ? screens.length - 1 : prev - 1));
  };

  const goToNextScreen = () => {
    setActiveScreen((prev) => (prev === screens.length - 1 ? 0 : prev + 1));
  };

  const currentScreen = screens[activeScreen];

  return (
    <div className="min-h-screen overflow-hidden bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <main className="flex-grow">
        <section
          id="presentation"
          className="relative isolate flex min-h-screen items-center overflow-hidden pt-28"
        >
          <div className="absolute inset-0 z-0 bg-slate-950" />

          <div
            className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url("${HERO_BACKGROUND_IMAGE}")`,
            }}
          />

          <div className="absolute inset-0 z-[1] bg-gradient-to-br from-slate-950/95 via-slate-950/78 to-slate-950/80" />

          <div className="absolute inset-0 z-[2] bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.30),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.22),transparent_36%)]" />

          <div className="absolute inset-0 z-[3] bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:44px_44px] opacity-20" />

          <div className="absolute left-[-120px] top-28 z-[4] h-80 w-80 rounded-full bg-primary/25 blur-3xl" />
          <div className="absolute bottom-10 right-[-120px] z-[4] h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />

          <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-12 px-4 pb-16 pt-8 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
            <Reveal variant="left">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-white/80 shadow-2xl backdrop-blur-xl">
                <HiSparkles className="text-primary" />
                Solution web pour clubs sportifs
              </div>

              <div className="mt-7 max-w-4xl">
                <h1 className="font-barlow text-5xl font-extrabold leading-[0.95] tracking-tight text-white drop-shadow-2xl sm:text-6xl lg:text-7xl xl:text-8xl">
                  MonAppliClub
                  <br />
                  Light
                </h1>

                <p className="mt-6 max-w-2xl text-base leading-8 text-white/85 drop-shadow sm:text-lg">
                  Une application simple et moderne pour présenter votre club
                  sportif, publier vos informations importantes, gérer vos
                  contenus et faciliter l’inscription des familles.
                </p>
              </div>

              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={scrollToFeatures}
                  className="group inline-flex items-center justify-center gap-3 rounded-full bg-primary px-7 py-4 text-sm font-bold text-white shadow-[0_20px_60px_rgba(242,113,49,0.35)] transition hover:-translate-y-0.5 hover:brightness-110"
                >
                  Découvrir l’application
                  <FaArrowRight className="transition group-hover:translate-x-1" />
                </button>

                <button
                  type="button"
                  onClick={scrollToTarifs}
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-white/20 bg-white/10 px-7 py-4 text-sm font-bold text-white backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white/15"
                >
                  Voir les tarifs
                  <FaEuroSign className="transition group-hover:scale-110" />
                </button>
              </div>

              <div className="mt-10 grid max-w-2xl gap-3 sm:grid-cols-3">
                <div className="rounded-3xl border border-white/15 bg-white/10 p-4 backdrop-blur-xl">
                  <FaMobileAlt className="mb-3 text-xl text-primary" />
                  <p className="font-barlow text-2xl font-bold text-white">
                    100%
                  </p>
                  <p className="mt-1 text-xs font-medium text-white/65">
                    Responsive
                  </p>
                </div>

                <div className="rounded-3xl border border-white/15 bg-white/10 p-4 backdrop-blur-xl">
                  <FaCogs className="mb-3 text-xl text-primary" />
                  <p className="font-barlow text-2xl font-bold text-white">
                    Admin
                  </p>
                  <p className="mt-1 text-xs font-medium text-white/65">
                    Gestion simple
                  </p>
                </div>

                <div className="rounded-3xl border border-white/15 bg-white/10 p-4 backdrop-blur-xl">
                  <FaShieldAlt className="mb-3 text-xl text-primary" />
                  <p className="font-barlow text-2xl font-bold text-white">
                    Démo
                  </p>
                  <p className="mt-1 text-xs font-medium text-white/65">
                    Lecture seule
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal
              variant="right"
              delay={120}
              className="relative hidden lg:block"
            >
              <div className="relative ml-auto max-w-md">
                <div className="absolute -left-8 -top-8 h-32 w-32 rounded-full bg-primary/30 blur-2xl" />
                <div className="absolute -bottom-8 -right-8 h-40 w-40 rounded-full bg-blue-500/25 blur-2xl" />

                <div className="relative overflow-hidden rounded-[2.5rem] border border-white/15 bg-white/10 p-5 shadow-2xl backdrop-blur-2xl">
                  <div className="rounded-[2rem] border border-white/10 bg-white/10 p-5">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/50">
                          Dashboard
                        </p>
                        <p className="mt-1 font-barlow text-2xl font-bold text-white">
                          MonAppliClub Light
                        </p>
                      </div>

                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-xl text-white shadow-xl">
                        <FaLaptopCode />
                      </div>
                    </div>

                    <div className="mt-6 grid grid-cols-2 gap-3">
                      <div className="rounded-2xl bg-white/10 p-4">
                        <FaEye className="mb-3 text-primary" />
                        <p className="text-sm font-bold text-white">
                          Site vitrine
                        </p>
                        <p className="mt-1 text-xs text-white/55">
                          Visible par tous
                        </p>
                      </div>

                      <div className="rounded-2xl bg-white/10 p-4">
                        <FaUserShield className="mb-3 text-primary" />
                        <p className="text-sm font-bold text-white">Admin</p>
                        <p className="mt-1 text-xs text-white/55">
                          Gestion privée
                        </p>
                      </div>

                      <div className="rounded-2xl bg-white/10 p-4">
                        <FaImages className="mb-3 text-primary" />
                        <p className="text-sm font-bold text-white">Médias</p>
                        <p className="mt-1 text-xs text-white/55">
                          Photos & actus
                        </p>
                      </div>

                      <div className="rounded-2xl bg-white/10 p-4">
                        <FaLock className="mb-3 text-primary" />
                        <p className="text-sm font-bold text-white">Sécurisé</p>
                        <p className="mt-1 text-xs text-white/55">
                          Accès contrôlé
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 space-y-3">
                      <div className="flex items-center gap-3 rounded-2xl bg-white/10 p-4">
                        <FaCheckCircle className="text-primary" />
                        <p className="text-sm font-semibold text-white/85">
                          Une solution adaptée aux petits et moyens clubs
                        </p>
                      </div>

                      <div className="flex items-center gap-3 rounded-2xl bg-white/10 p-4">
                        <FaClock className="text-primary" />
                        <p className="text-sm font-semibold text-white/85">
                          Mise à jour rapide des contenus importants
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
        
        <section
          id="fonctionnalites"
          className="relative scroll-mt-28 bg-white py-24 dark:bg-slate-950"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="mx-auto max-w-3xl text-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-primary">
                  <FaStar />
                  Fonctionnalités
                </div>

                <h2 className="mt-6 font-barlow text-4xl font-extrabold tracking-tight text-slate-950 dark:text-white sm:text-5xl">
                  Tout ce qu’il faut pour présenter clairement votre club.
                </h2>

                <p className="mt-5 text-base leading-8 text-slate-600 dark:text-slate-400">
                  MonAppliClub Light permet de créer une vitrine moderne et
                  administrable, sans transformer la gestion du club en usine à
                  gaz.
                </p>
              </div>
            </Reveal>

            <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {features.map((feature, index) => {
                const Icon = feature.icon;

                return (
                  <Reveal key={feature.title} delay={index * 60}>
                    <article className="group h-full overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900">
                      <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-xl text-primary transition group-hover:bg-primary group-hover:text-white">
                        <Icon />
                      </div>

                      <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
                        {feature.title}
                      </h3>

                      <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-400">
                        {feature.text}
                      </p>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-slate-100 py-24 dark:bg-slate-900/70">
          <div className="absolute left-[-120px] top-20 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute bottom-0 right-[-120px] h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />

          <div className="relative mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
            <Reveal variant="left">
              <div className="sticky top-28">
                <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-primary shadow-sm dark:bg-slate-950">
                  <FaRegLightbulb />
                  Modules disponibles
                </div>

                <h2 className="mt-6 font-barlow text-4xl font-extrabold tracking-tight text-slate-950 dark:text-white sm:text-5xl">
                  Une base complète pour lancer rapidement le site du club.
                </h2>

                <p className="mt-5 text-base leading-8 text-slate-600 dark:text-slate-400">
                  Les pages essentielles sont prévues pour que le club puisse
                  communiquer simplement avec ses adhérents, prospects, familles
                  et partenaires.
                </p>
              </div>
            </Reveal>

            <Reveal variant="right" delay={120}>
              <div className="grid gap-3 sm:grid-cols-2">
                {modules.map((module) => (
                  <div
                    key={module}
                    className="flex items-center gap-3 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <FaCheckCircle />
                    </span>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                      {module}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
        <section className="relative overflow-hidden bg-white py-24 dark:bg-slate-950">
          <div className="absolute left-[-120px] top-20 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute bottom-0 right-[-120px] h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="overflow-hidden rounded-[2.5rem] border border-slate-200 bg-slate-950 shadow-2xl dark:border-slate-800">
                <div className="relative">
                  <img
                    src="/images/illustration_monappliclublight.webp"
                    alt="Un président de club gère son club de judo avec MonAppliClub Light pendant que des parents consultent l'application sur mobile"
                    loading="lazy"
                    decoding="async"
                    className="h-[360px] w-full object-cover sm:h-[460px] lg:h-[560px]"
                  />

                  <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/45 to-slate-950/10" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />

                  <div className="absolute inset-0 flex items-end">
                    <div className="max-w-2xl p-6 sm:p-8 lg:p-12">
                      <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-white/80 backdrop-blur-xl">
                        <FaLaptopCode className="text-primary" />
                        Une application pensée pour le terrain
                      </div>

                      <h2 className="mt-5 font-barlow text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                        Le club garde la main, les familles trouvent
                        l’information.
                      </h2>

                      <p className="mt-4 max-w-xl text-sm leading-7 text-white/75 sm:text-base">
                        MonAppliClub Light facilite la communication du club :
                        le responsable met à jour les contenus depuis son
                        ordinateur ou smartphone, et les parents consultent les
                        informations utiles directement depuis leur téléphone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section
          id="screens"
          className="scroll-mt-28 bg-white py-24 dark:bg-slate-950"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <div className="max-w-3xl">
                  <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-primary">
                    <FaImages />
                    Captures d’écran
                  </div>

                  <h2 className="mt-6 font-barlow text-4xl font-extrabold tracking-tight text-slate-950 dark:text-white sm:text-5xl">
                    Une interface claire côté public comme côté admin.
                  </h2>

                  <p className="mt-5 text-base leading-8 text-slate-600 dark:text-slate-400">
                    Présentation des principales pages de MonAppliClub Light. Le
                    design est pensé pour être moderne, rassurant et facile à
                    prendre en main, que ce soit pour les familles ou les
                    responsables du club.
                  </p>
                </div>

                <a
                  href="/appli/dashboard"
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-slate-950 px-6 py-4 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-primary dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
                >
                  Voir la démo
                  <FaPlayCircle />
                </a>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="mt-12 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-slate-950 p-3 shadow-2xl dark:border-slate-800 sm:p-4">
                <div className="rounded-[2rem] border border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.22),transparent_34%),linear-gradient(135deg,#0f172a,#020617)] p-4 sm:p-6 lg:p-8">
                  <div className="mb-6 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-red-400" />
                      <span className="h-3 w-3 rounded-full bg-yellow-400" />
                      <span className="h-3 w-3 rounded-full bg-green-400" />
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={goToPrevScreen}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white transition hover:bg-white/20"
                        aria-label="Capture précédente"
                      >
                        <FaChevronLeft />
                      </button>

                      <button
                        type="button"
                        onClick={goToNextScreen}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white transition hover:bg-white/20"
                        aria-label="Capture suivante"
                      >
                        <FaChevronRight />
                      </button>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white p-2 shadow-2xl dark:bg-slate-900 sm:p-3">
                    <div className="overflow-hidden rounded-[1.5rem] bg-slate-100 dark:bg-slate-950">
                      <img
                        src={currentScreen.image}
                        alt={currentScreen.title}
                        loading="lazy"
                        decoding="async"
                        className="aspect-[16/9] w-full object-contain object-top"
                      />
                    </div>
                  </div>

                  <div className="mt-4 rounded-[1.5rem] border border-white/10 bg-white/10 px-5 py-4 backdrop-blur-xl">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
                          {currentScreen.category}
                        </p>

                        <h3 className="mt-1 truncate font-barlow text-xl font-bold text-white sm:text-2xl">
                          {currentScreen.title}
                        </h3>
                      </div>

                      <div className="lg:max-w-2xl lg:border-l lg:border-white/10 lg:pl-5">
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">
                          Commentaire
                        </p>

                        <p className="mt-1 text-sm leading-6 text-white">
                          {currentScreen.comment}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-7 rounded-[2rem] border border-white/10 bg-white/10 p-4">
                    <div className="mb-4 flex items-center justify-between gap-4">
                      <p className="text-sm font-bold text-white">
                        Toutes les captures
                      </p>

                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/45">
                        {String(activeScreen + 1).padStart(2, "0")} /{" "}
                        {String(screens.length).padStart(2, "0")}
                      </p>
                    </div>

                    <div className="flex gap-3 overflow-x-auto pb-2">
                      {screens.map((screen, index) => (
                        <button
                          key={`${screen.title}-${index}`}
                          type="button"
                          onClick={() => setActiveScreen(index)}
                          className={`group w-52 shrink-0 overflow-hidden rounded-2xl border text-left transition sm:w-64 ${
                            activeScreen === index
                              ? "border-primary ring-2 ring-primary/40"
                              : "border-white/10 hover:border-white/25"
                          }`}
                        >
                          <div className="bg-white p-1 dark:bg-slate-900">
                            <div className="overflow-hidden rounded-xl bg-slate-100 dark:bg-slate-950">
                              <img
                                src={screen.image}
                                alt={screen.title}
                                loading="lazy"
                                decoding="async"
                                className="aspect-[16/9] w-full object-contain object-top transition duration-300 group-hover:scale-[1.02]"
                              />
                            </div>
                          </div>

                          <div className="bg-slate-950/80 p-3">
                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
                              {screen.category}
                            </p>
                            <p className="mt-1 line-clamp-2 text-xs font-bold text-white">
                              {screen.title}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>

                    <div className="mt-5 flex items-center justify-center gap-2">
                      {screens.map((_, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setActiveScreen(index)}
                          className="text-xs text-white/70 transition hover:text-primary"
                          aria-label={`Aller à la capture ${index + 1}`}
                        >
                          {activeScreen === index ? (
                            <FaCircle />
                          ) : (
                            <FaRegCircle />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section
          id="tarifs"
          className="relative scroll-mt-28 overflow-hidden bg-slate-100 py-24 dark:bg-slate-900/70"
        >
          <div className="absolute left-[-120px] top-20 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute bottom-0 right-[-120px] h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="mx-auto max-w-3xl text-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-primary shadow-sm dark:bg-slate-950">
                  <FaEuroSign />
                  Tarif unique
                </div>

                <h2 className="mt-6 font-barlow text-4xl font-extrabold tracking-tight text-slate-950 dark:text-white sm:text-5xl">
                  Une solution complète à prix accessible pour les clubs
                  sportifs.
                </h2>

                <p className="mt-5 text-base leading-8 text-slate-600 dark:text-slate-400">
                  MonAppliClub Light a été pensé pour les petites et moyennes
                  associations qui veulent un site moderne, simple à gérer, sans
                  frais techniques compliqués ni abonnement difficile à suivre.
                </p>
              </div>
            </Reveal>

            <div className="mt-14 grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-stretch">
              <Reveal variant="left">
                <article className="relative flex h-full flex-col overflow-hidden rounded-[2.5rem] border border-primary bg-slate-950 p-7 text-white shadow-2xl dark:bg-white dark:text-slate-950">
                  <div className="absolute right-[-80px] top-[-80px] h-56 w-56 rounded-full bg-primary/30 blur-3xl" />
                  <div className="absolute bottom-[-100px] left-[-80px] h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" />

                  <div className="relative">
                    <div className="inline-flex rounded-full bg-primary px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white">
                      Tout compris
                    </div>

                    <h3 className="mt-7 font-barlow text-4xl font-extrabold tracking-tight sm:text-5xl">
                      Formule Club
                    </h3>

                    <p className="mt-4 text-sm leading-7 text-white/72 dark:text-slate-600">
                      Une offre annuelle claire pour permettre au club de
                      disposer d’un vrai site internet moderne, hébergé,
                      maintenu et accompagné.
                    </p>

                    <div className="mt-8 flex items-end gap-2">
                      <span className="font-barlow text-7xl font-extrabold leading-none">
                        200€
                      </span>
                      <span className="pb-2 text-base font-bold text-white/65 dark:text-slate-500">
                        / an
                      </span>
                    </div>

                    <p className="mt-3 text-sm font-semibold text-primary">
                      Site + domaine + hébergement + formation + accompagnement
                    </p>

                    <div className="mt-8 rounded-[2rem] border border-white/10 bg-white/10 p-5 dark:border-slate-200 dark:bg-slate-100">
                      <p className="text-sm font-bold text-white dark:text-slate-950">
                        L’objectif est simple :
                      </p>
                      <p className="mt-2 text-sm leading-7 text-white/72 dark:text-slate-600">
                        permettre à votre club d’être visible, crédible et
                        autonome dans sa communication, sans devoir gérer la
                        partie technique.
                      </p>
                    </div>

                    <button
                      type="button"
                      className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-primary px-7 py-4 text-sm font-bold text-white shadow-[0_20px_60px_rgba(242,113,49,0.25)] transition hover:-translate-y-0.5 hover:brightness-110"
                    >
                      Demander une présentation
                      <FaArrowRight />
                    </button>
                  </div>
                </article>
              </Reveal>

              <Reveal variant="right" delay={120}>
                <div className="grid h-full gap-5 sm:grid-cols-2">
                  <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                    <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-xl text-primary">
                      <FaLaptopCode />
                    </div>
                    <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
                      Mise en place du site
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-400">
                      Installation, configuration de la vitrine, ajout des
                      premières informations et adaptation aux couleurs du club.
                    </p>
                  </article>

                  <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                    <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-xl text-primary">
                      <FaShieldAlt />
                    </div>
                    <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
                      Domaine & hébergement
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-400">
                      Réservation annuelle du nom de domaine, hébergement du
                      site, serveur et gestion technique inclus dans la formule.
                    </p>
                  </article>

                  <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                    <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-xl text-primary">
                      <FaUsers />
                    </div>
                    <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
                      Formation en visio
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-400">
                      Une formation à distance pour montrer aux utilisateurs
                      comment gérer les contenus, les actualités, les photos et
                      les informations.
                    </p>
                  </article>

                  <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                    <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-xl text-primary">
                      <FaClock />
                    </div>
                    <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
                      Suivi toute l’année
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-400">
                      Accompagnement, conseils, suivi technique et aide en cas
                      de besoin tout au long de l’année.
                    </p>
                  </article>
                </div>
              </Reveal>
            </div>

            <Reveal delay={180}>
              <div className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="mt-1 shrink-0 text-primary" />
                    <div>
                      <p className="text-sm font-bold text-slate-950 dark:text-white">
                        Aucun coût caché
                      </p>
                      <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-400">
                        Le club connaît son budget annuel dès le départ.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="mt-1 shrink-0 text-primary" />
                    <div>
                      <p className="text-sm font-bold text-slate-950 dark:text-white">
                        Pensé pour les bénévoles
                      </p>
                      <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-400">
                        Une interface simple, sans compétence technique
                        nécessaire.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="mt-1 shrink-0 text-primary" />
                    <div>
                      <p className="text-sm font-bold text-slate-950 dark:text-white">
                        Adapté aux petits clubs
                      </p>
                      <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-400">
                        Un tarif volontairement accessible pour les
                        associations.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="relative overflow-hidden bg-slate-950 py-24">
          <div className="absolute left-[-120px] top-10 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute bottom-[-160px] right-[-120px] h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />

          <Reveal>
            <div className="relative mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-white/75 backdrop-blur-xl">
                <HiSparkles className="text-primary" />
                Prêt pour une démonstration ?
              </div>

              <h2 className="mt-6 font-barlow text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
                Découvrez MonAppliClub Light comme si vous étiez administrateur.
              </h2>

              <p className="mx-auto mt-5 max-w-3xl text-base leading-8 text-white/70">
                Le mode démo permet de parcourir le Dashboard Admin en lecture
                seule afin de comprendre rapidement le fonctionnement de
                l’application.
              </p>

              <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
                <a
                  href="/appli/dashboard"
                  className="group inline-flex items-center justify-center gap-3 rounded-full bg-primary px-7 py-4 text-sm font-bold text-white shadow-[0_20px_60px_rgba(242,113,49,0.35)] transition hover:-translate-y-0.5 hover:brightness-110"
                >
                  Lancer la démo Admin
                  <FaArrowRight className="transition group-hover:translate-x-1" />
                </a>

                <a
                  href="/"
                  className="inline-flex items-center justify-center gap-3 rounded-full border border-white/20 bg-white/10 px-7 py-4 text-sm font-bold text-white backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white/15"
                >
                  Voir le site vitrine
                  <FaEye />
                </a>
              </div>
            </div>
          </Reveal>
        </section>
        <Footer />
        <ScrollIndicator />
      </main>
    </div>
  );
}
